/**
 * LA PERSONALIZACION — el texto habla a una sola persona.
 *
 * Cada canalizacion viene escrita con dos clases de marcador:
 *
 *   {NOMBRE}                  el nombre del lector
 *   {masculino/femenino}      concordancia de genero, masculino primero
 *
 * En las 94 piezas publicadas hay 169 marcadores distintos y 294 apariciones
 * de {NOMBRE}. Todos siguen esas dos formas: no hay ningun caso especial.
 *
 * Segun el plan del lector:
 *   Sin cuenta          el texto no lo llama de ninguna manera. Se retiran el
 *                       nombre y el vocativo que lo acompanaba —suelto queda
 *                       cojo: «Querido, soy el Arcangel Jofiel»— y el texto le
 *                       habla de tu, que es como esta escrito.
 *   Plan 1 en adelante  el texto lo llama por su nombre (desde el registro
 *                       gratuito: Faina, 07-10-2026; antes era desde el plan 2).
 *
 * Los vocativos que el texto trae por su cuenta, sin {NOMBRE} al lado, se
 * respetan siempre: son de Faina, no un resto de la personalizacion.
 */

/** Como trata cada entidad a quien la escucha (definido en la vara del proyecto). */
const VOCATIVOS = {
  'Jeshua':                 { m: 'hermano',  f: 'hermana' },
  'María Magdalena':        { m: 'hermano',  f: 'hermana' },
  'María':                  { m: 'hijo',     f: 'hija' },
  'Ana':                    { m: 'hijo',     f: 'hija' },
  'Trabajadores de la Luz': { m: 'hermano',  f: 'hermana' }
};
const VOCATIVO_ANGELICO = { m: 'amado', f: 'amada' };

export const vocativoDe = (entidad, genero) => {
  const v = VOCATIVOS[entidad] || VOCATIVO_ANGELICO;
  return genero === 'f' ? v.f : v.m;
};

/** Raices de los vocativos con los que una entidad llama al lector. */
const RAIZ_VOCATIVO = '(?:Querid|querid|Amad|amad|Herman|herman|Hij|hij|Amig|amig|Niet|niet|Maestr|maestr)';

/**
 * Vocativo de apertura. En las 94 piezas hay 146 apariciones y solo diez
 * formas, todas de trato directo:
 *
 *   {Amado/Amada} {NOMBRE}          {Hijo/Hija} de mi corazon
 *   {Querido/Querida} {NOMBRE}      {Hijo/Hija} {mio/mia}
 *   {Amado/Amada}                   {Hermano/Hermana} de mi corazon
 *   {Hermano/Hermana}               {Amado/Amada} {hermano/hermana}
 *   {Maestro/Maestra}               {Hijo mio/Hija mia}
 *
 * En el plan gratuito se retiran todas: llamar al lector es justamente lo que
 * ese plan no hace. Se exige que abran parrafo o frase y que terminen en coma
 * o dos puntos; un vocativo dentro de la frase («Descansa, hijo mio») es parte
 * del texto y se respeta.
 */
/**
 * Los saludos de los canalizadores del corpus (lote 1, 05-10-2026) traen un
 * posesivo delante: «{Mi querido amigo/Mi querida amiga} {NOMBRE}:». Sin
 * aceptarlo, la version sin nombre dejaba «Mi querido», cojo. El «Mi» puede ir
 * dentro o fuera de la llave.
 */
const POSESIVO = '(?:[Mm]is?\\s+)?';

const VOCATIVO_DE_APERTURA = new RegExp(
  '(^|[.!?»]s+|«)' + POSESIVO + '\\{' + POSESIVO + RAIZ_VOCATIVO + '[^{}]*\\}[^,.:;!?]{0,42}?\\s*[,:]\\s*', 'g'
);
// ⚠️ El «s+» de arriba es literal a proposito: el patron original se escribio
// con '\s' dentro de una cadena, que JavaScript lee como una «s», y asi ha
// funcionado siempre en las 297. Corregirlo retira vocativos a mitad de
// parrafo en textos ya aprobados; no se toca sin Faina.

/**
 * Vocativos que pueden ir pegados al nombre y se retiran junto con el, con su
 * posesivo y hasta tres palabras de trato: «mi querido amigo {NOMBRE}».
 */
const VOCATIVO_PEGADO = new RegExp(
  '\\b' + POSESIVO + '(?:' + RAIZ_VOCATIVO + '[a-záéíóúñ]*\\s+){1,3}\\{NOMBRE\\}', 'g'
);

/** Marca temporal del sitio donde estaba el nombre, para poder limpiar alrededor. */
const HUECO = '@@N@@';

const limpiarHueco = (texto) => {
  let t = texto;
  t = t.replace(new RegExp(',\\s*' + HUECO + '\\s*,\\s*', 'g'), ' ');   // «los milagros, X, nunca» -> «los milagros nunca»
  t = t.replace(new RegExp('\\s*' + HUECO + '\\s*[,;:]\\s*', 'g'), '');  // «X, soy Jofiel» -> «soy Jofiel»
  t = t.replace(new RegExp('[,;:]\\s*' + HUECO + '\\s*', 'g'), '');      // «y ahora, X» -> «y ahora»
  t = t.replace(new RegExp('\\s*' + HUECO + '\\s*', 'g'), ' ');
  t = t.replace(/\s{2,}/g, ' ').replace(/\s+([.!?,;:])/g, '$1').trim();
  // Si la frase quedo empezando en minuscula, se recupera la mayuscula.
  t = t.replace(/(^|[.!?»]\s+|«)([a-záéíóúñ])/g, (m, antes, letra) => antes + letra.toUpperCase());
  return t;
};

/**
 * Resuelve los marcadores de un parrafo.
 *
 * @param {string} texto     el parrafo tal como viene del documento
 * @param {object} lector    { nombre, genero }  genero: 'm' | 'f'
 * @param {string} entidad   quien habla
 * @param {boolean} personalizar  false en el plan gratuito
 */
export const resolver = (texto, lector = {}, entidad = '', personalizar = true) => {
  const genero = lector.genero === 'f' ? 'f' : 'm';
  const nombre = personalizar ? (lector.nombre || '').trim() : '';
  let t = texto;

  // 1. Concordancia de genero: {masculino/femenino}
  t = t.replace(/\{([^{}/]+)\/([^{}/]+)\}/g, (_, masc, fem) => (genero === 'f' ? fem : masc));

  if (nombre) {
    t = t.replace(/\{NOMBRE\}/g, nombre);
  } else {
    // El vocativo que abre parrafo o frase se retira antes de resolver generos.
    t = texto.replace(VOCATIVO_DE_APERTURA, '$1');
    t = t.replace(/\{([^{}/]+)\/([^{}/]+)\}/g, (_, masc, fem) => (genero === 'f' ? fem : masc));
    t = t.replace(VOCATIVO_PEGADO, HUECO);
    t = t.replace(/\{NOMBRE\}/g, HUECO);
    t = limpiarHueco(t);
  }

  return t;
};

/** Resuelve una lista de parrafos de una vez. */
export const resolverTexto = (parrafos = [], lector, entidad, personalizar = true) =>
  parrafos.map((p) => resolver(p, lector, entidad, personalizar));

/**
 * Devuelve los trozos de un parrafo separando el nombre del lector, para poder
 * pintarlo con el color del rayo de la entidad sin recurrir a HTML crudo.
 * Cada trozo es { texto, esNombre }.
 */
export const trozosConNombre = (texto, nombre) => {
  if (!nombre) return [{ texto, esNombre: false }];
  const partes = texto.split(nombre);
  if (partes.length === 1) return [{ texto, esNombre: false }];
  const trozos = [];
  partes.forEach((p, i) => {
    if (p) trozos.push({ texto: p, esNombre: false });
    if (i < partes.length - 1) trozos.push({ texto: nombre, esNombre: true });
  });
  return trozos;
};
